import {IFormElement, IFormSection} from "~/interfaces/formSubmissionEditInterfaces"
import FormElementControlGroups from "~/models/formElements/formElementControlGroups"
import {RegexHelper} from "~/helpers/regexHelper"
import {Hash} from "~/interfaces/hashInterfaces"
import {ICache} from "~/interfaces/cacheInterfaces"

interface IFormSubmissionConditionalRuleValueOpts {
  /**
   * Contains form elements
   */
  formSection: IFormSection,
  /**
   * What dictionary of elements should found elements be stored in
   * Meant to be a way to only have to dig into the form section to find the element once
   */
  elementDictionary: ICache<Hash<IFormElement>>

  /**
   * How to find the form element within the form section
   */
  elementFinder(clientId: string, formSection: IFormSection): IFormElement,
}

/**
 * Use this class to turn a value that is referenced in conditional logic into the value usable by said logic
 * @example take value for a checkbox group (option client_ids) and turn into label_titles
 */
export default class FormSubmissionConditionalRuleValue {
  /**
   *
   * @param opts {IFormSubmissionConditionalRuleValueOpts}
   */
  constructor(private opts: IFormSubmissionConditionalRuleValueOpts) {
  }

  /**
   * @param identifier usually element client_id but occasionally the label_title/reference_name
   */
  private static getElement(identifier, opts: IFormSubmissionConditionalRuleValueOpts): IFormElement {
    return opts.elementDictionary.cached(identifier, () => {
      return opts.elementFinder(identifier, opts.formSection)
    })
  }

  private static getValue(referencedValue: string, clientId: string, opts: IFormSubmissionConditionalRuleValueOpts): unknown {
    const element = this.getElement(clientId, opts)

    if (!element) return referencedValue

    // if element is a selectable option (radio/dropdown item/checkbox)
    // then return its label_title
    if (FormElementControlGroups.SUBREFERENCEABLE_CONTROLS.includes(element.element_type)) {
      return element.label_title
    } else if (RegexHelper.clientId.test(referencedValue?.toString())) {
      // if value is a client_id, then it could correspond to a pickable value (checkbox/radio/dropdown item)
      // if so, then return its label_title
      const valueElement = opts.elementDictionary.cached(referencedValue.toString(), () => {
        return opts.elementFinder(referencedValue.toString(), opts.formSection)
      })
      if (valueElement) return valueElement.label_title
    }
  }

  /**
   * @param identifier usually element client_id but occasionally the label_title/reference_name
   */
  getElement(identifier: string): IFormElement {
    return FormSubmissionConditionalRuleValue.getElement(identifier, this.opts)
  }

  /**
   *
   * @param referenceValue could match a form element client_id
   * @param clientId the key for which the referenceValue is for
   */
  getValue(referenceValue: string, clientId: string) {
    return FormSubmissionConditionalRuleValue.getValue(referenceValue, clientId, this.opts)
  }
}